// web/src/pages/payments/RecordPaymentModal.tsx
//
// I-4 — 4-step "Record payment" modal wizard.
//
// Step 1 (Details):   amount, transactionDate, paymentTypeCode, paymentMethodCode,
//                     paymentGatewayCode, pymtReferenceNo, notes.
// Step 2 (Allocation): single-invoice (when invoiceId prop set) or open-invoices
//                     multi-select grid (generic flow with accountId input).
// Step 3 (Review):    totals + overpayment warning.
// Step 4 (Submit):    POST /api/payments; 202 → toast + close.
//
// Launched from InvoiceDetailPage (invoice context) or PaymentsListPage (generic).
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPayment,
  listInvoices,
  listPaymentTypeLookups,
  listPaymentMethodLookups,
  listPaymentGatewayLookups,
  type CreatePaymentRequest,
  type InvoiceSummaryDto,
} from "@/lib/invoices-payments";
import {
  PaymentAllocationGrid,
  type AllocationRow,
} from "@/components/payment-allocation-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RecordPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When launched from I-2, pre-fill the modal with this invoice context. */
  invoiceId?: number;
  accountId?: number;
  /** Default amount to pre-fill (e.g. balanceOutstanding from InvoiceDetailPage). */
  defaultAmount?: number;
  /** Called after a successful submit so the caller can refresh relevant queries. */
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Step type
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3 | 4;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function newIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Step indicator breadcrumb
function StepIndicator({ current }: { current: Step }) {
  const steps: { n: Step; label: string }[] = [
    { n: 1, label: "Details" },
    { n: 2, label: "Allocation" },
    { n: 3, label: "Review" },
    { n: 4, label: "Submit" },
  ];
  return (
    <ol className="flex w-full items-center text-sm">
      {steps.map((s, i) => {
        const isPast = s.n < current;
        const isCurrent = s.n === current;
        return (
          <li key={s.n} className={cn("flex items-center", i !== steps.length - 1 && "flex-1")}>
            <div className="flex items-center gap-2">
              <div className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors",
                isCurrent ? "bg-primary text-primary-foreground" : 
                isPast ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
              )}>
                {isPast ? <Check className="h-3.5 w-3.5" /> : s.n}
              </div>
              <span className={cn(
                "font-medium transition-colors hidden sm:block", 
                isCurrent ? "text-primary" : "text-muted-foreground"
              )}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="mx-2 h-px flex-1 bg-border sm:mx-4" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// RecordPaymentModal
// ---------------------------------------------------------------------------

export function RecordPaymentModal({
  open,
  onOpenChange,
  invoiceId: invoiceIdProp,
  accountId: accountIdProp,
  defaultAmount,
  onSuccess,
}: RecordPaymentModalProps) {
  const qc = useQueryClient();

  // ----- Step 1 state -------------------------------------------------------
  const [amount, setAmount] = useState<string>(
    defaultAmount != null ? String(defaultAmount) : "",
  );
  const [transactionDate, setTransactionDate] = useState<string>(todayIso());
  const [paymentTypeCode, setPaymentTypeCode] = useState<string>("");
  const [paymentMethodCode, setPaymentMethodCode] = useState<string>("");
  const [paymentGatewayCode, setPaymentGatewayCode] = useState<string>("");
  const [pymtReferenceNo, setPymtReferenceNo] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  // ----- Step 2 state -------------------------------------------------------
  // Generic flow: allow user to enter accountId manually when no prop given.
  const [manualAccountId, setManualAccountId] = useState<string>(
    accountIdProp != null ? String(accountIdProp) : "",
  );
  const resolvedAccountId: number | null = accountIdProp ?? (manualAccountId.trim() !== "" ? Number(manualAccountId) : null);

  // Allocation rows — one per open invoice.
  const [allocationRows, setAllocationRows] = useState<AllocationRow[]>([]);
  const [allocationInitialised, setAllocationInitialised] = useState(false);

  // ----- Backend-sourced lookup options for Step 1 dropdowns ----------------
  const typesQ = useQuery({ queryKey: ["payment-types"], queryFn: listPaymentTypeLookups });
  const methodsQ = useQuery({ queryKey: ["payment-methods"], queryFn: listPaymentMethodLookups });
  const gatewaysQ = useQuery({ queryKey: ["payment-gateways"], queryFn: listPaymentGatewayLookups });

  // ----- Step navigation ----------------------------------------------------
  const [step, setStep] = useState<Step>(1);

  // ----- Open invoices query for generic (non-invoice-context) flow ---------
  const openInvoicesEnabled =
    open && step === 2 && invoiceIdProp == null && resolvedAccountId != null;

  const { data: openInvoicesData, isLoading: loadingOpenInvoices } = useQuery({
    queryKey: ["open-invoices-for-payment", resolvedAccountId],
    queryFn: () =>
      listInvoices({
        accountId: resolvedAccountId!,
        status: ["Sent", "PartPaid", "Overdue"],
        pageSize: 200,
      }),
    enabled: openInvoicesEnabled,
  });

  // Initialise allocation rows once open invoices load (generic flow).
  useMemo(() => {
    if (invoiceIdProp != null || !openInvoicesData) return;
    if (allocationInitialised) return;
    const rows: AllocationRow[] = openInvoicesData.items.map(
      (inv: InvoiceSummaryDto) => ({
        invoiceId: inv.invoiceId,
        invoiceRef: inv.invoiceReferenceNo,
        balance: inv.balanceOutstanding,
        allocate: 0,
      }),
    );
    setAllocationRows(rows);
    setAllocationInitialised(true);
  }, [openInvoicesData, invoiceIdProp, allocationInitialised]);

  // ----- Derived totals for Step 3 ------------------------------------------
  const paymentAmount = Number(amount);

  const totalAllocated = useMemo(() => {
    if (invoiceIdProp != null) {
      // Single-invoice: the full payment amount is allocated to this invoice.
      return paymentAmount;
    }
    return allocationRows.reduce((sum, r) => sum + r.allocate, 0);
  }, [invoiceIdProp, paymentAmount, allocationRows]);

  const overPayment = useMemo(
    () => (Number.isFinite(totalAllocated) && Number.isFinite(paymentAmount)
      ? Math.max(0, totalAllocated - paymentAmount)
      : 0),
    [totalAllocated, paymentAmount],
  );

  const underAllocation = useMemo(
    () => (Number.isFinite(paymentAmount) && Number.isFinite(totalAllocated)
      ? Math.max(0, paymentAmount - totalAllocated)
      : 0),
    [paymentAmount, totalAllocated],
  );

  // ----- Mutation -----------------------------------------------------------
  const createMut = useMutation({
    mutationFn: (body: CreatePaymentRequest) => createPayment(body),
    onSuccess: () => {
      toast.info("Payment submitted — processing in background.");
      void qc.invalidateQueries({ queryKey: ["payments"] });
      void qc.invalidateQueries({ queryKey: ["invoice-payments"] });
      if (invoiceIdProp != null) {
        void qc.invalidateQueries({ queryKey: ["invoice", invoiceIdProp] });
      }
      onSuccess?.();
      handleClose();
    },
    onError: () => toast.error("Failed to submit payment."),
  });

  // ----- Validation ---------------------------------------------------------
  const step1Valid =
    paymentAmount > 0 &&
    Number.isFinite(paymentAmount) &&
    transactionDate.trim() !== "" &&
    paymentTypeCode.trim() !== "";

  // ----- Step 1 -> Step 2 transition ----------------------------------------
  function goToStep2() {
    // If invoice context provided, pre-set a single-row allocation.
    if (invoiceIdProp != null && !allocationInitialised) {
      setAllocationRows([
        {
          invoiceId: invoiceIdProp,
          invoiceRef: `Invoice #${invoiceIdProp}`,
          balance: defaultAmount ?? paymentAmount,
          allocate: paymentAmount,
        },
      ]);
      setAllocationInitialised(true);
    }
    setStep(2);
  }

  // ----- Build payload ------------------------------------------------------
  function buildPayload(): CreatePaymentRequest {
    const allocations = invoiceIdProp != null
      ? [{ invoiceId: invoiceIdProp, amount: paymentAmount }]
      : allocationRows
          .filter((r) => r.allocate > 0)
          .map((r) => ({ invoiceId: r.invoiceId, amount: r.allocate }));

    return {
      invoiceId: invoiceIdProp ?? null,
      accountId: resolvedAccountId,
      amount: paymentAmount,
      transactionDate,
      paymentTypeCode,
      paymentMethodCode: paymentMethodCode.trim() || null,
      paymentGatewayCode: paymentGatewayCode.trim() || null,
      pymtReferenceNo: pymtReferenceNo.trim() || null,
      notes: notes.trim() || null,
      idempotencyKey: newIdempotencyKey(),
      allocations: allocations.length > 0 ? allocations : null,
    };
  }

  // ----- Close / reset ------------------------------------------------------
  function handleClose() {
    onOpenChange(false);
    // Reset after a short delay so the closing animation plays cleanly.
    setTimeout(() => {
      setStep(1);
      setAmount(defaultAmount != null ? String(defaultAmount) : "");
      setTransactionDate(todayIso());
      setPaymentTypeCode("");
      setPaymentMethodCode("");
      setPaymentGatewayCode("");
      setPymtReferenceNo("");
      setNotes("");
      setManualAccountId(accountIdProp != null ? String(accountIdProp) : "");
      setAllocationRows([]);
      setAllocationInitialised(false);
    }, 300);
  }

  // ----- Render -------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl h-[750px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>

        <StepIndicator current={step} />

        {/* ------------------------------------------------------------------ */}
        {/* Step 1: Details                                                      */}
        {/* ------------------------------------------------------------------ */}
        {step === 1 && (
          <section aria-label="Step 1: Details" className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-1 pr-2 space-y-4">
              <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="pm-amount">Amount *</Label>
                <Input
                  id="pm-amount"
                  type="number"
                  min={0}
                  step={0.01}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="pm-date">Transaction date *</Label>
                <Input
                  id="pm-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="pm-type">Payment type *</Label>
                <Select value={paymentTypeCode} onValueChange={setPaymentTypeCode}>
                  <SelectTrigger
                    id="pm-type"
                    aria-label="Payment type"
                    disabled={typesQ.isLoading}
                  >
                    <SelectValue
                      placeholder={typesQ.isLoading ? "Loading…" : "Select a type"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(typesQ.data ?? [])
                      .filter((t) => t.active)
                      .map((t) => (
                        <SelectItem key={t.code} value={t.code}>
                          {t.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pm-method">Payment method</Label>
                <Select value={paymentMethodCode} onValueChange={setPaymentMethodCode}>
                  <SelectTrigger
                    id="pm-method"
                    aria-label="Payment method"
                    disabled={methodsQ.isLoading}
                  >
                    <SelectValue
                      placeholder={methodsQ.isLoading ? "Loading…" : "Optional"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(methodsQ.data ?? [])
                      .filter((m) => m.active)
                      .map((m) => (
                        <SelectItem key={m.code} value={m.code}>
                          {m.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pm-gateway">Payment gateway</Label>
                <Select value={paymentGatewayCode} onValueChange={setPaymentGatewayCode}>
                  <SelectTrigger
                    id="pm-gateway"
                    aria-label="Payment gateway"
                    disabled={gatewaysQ.isLoading}
                  >
                    <SelectValue
                      placeholder={gatewaysQ.isLoading ? "Loading…" : "Optional"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(gatewaysQ.data ?? [])
                      .filter((g) => g.active)
                      .map((g) => (
                        <SelectItem key={g.code} value={g.code}>
                          {g.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pm-ref">Payment reference no.</Label>
                <Input
                  id="pm-ref"
                  value={pymtReferenceNo}
                  onChange={(e) => setPymtReferenceNo(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pm-notes">Notes</Label>
              <textarea
                id="pm-notes"
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>

            {/* Generic flow: show account input when no invoice context */}
            {invoiceIdProp == null && (
              <div>
                <Label htmlFor="pm-account">Account ID</Label>
                <Input
                  id="pm-account"
                  type="number"
                  value={manualAccountId}
                  onChange={(e) => {
                    setManualAccountId(e.target.value);
                    setAllocationInitialised(false);
                    setAllocationRows([]);
                  }}
                  placeholder="Enter account ID to load open invoices"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Leave blank for a direct credit with no invoice allocation.
                </p>
              </div>
            )}

            </div>
            <div className="flex justify-end pt-4 shrink-0 mt-auto">
              <Button disabled={!step1Valid} onClick={goToStep2}>
                Next
              </Button>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Step 2: Allocation                                                   */}
        {/* ------------------------------------------------------------------ */}
        {step === 2 && (
          <section aria-label="Step 2: Allocation" className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-1 pr-2 space-y-4">
            {invoiceIdProp != null ? (
              /* Single-invoice context: show a locked row */
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  This payment will be allocated to invoice #{invoiceIdProp}.
                </p>
                <PaymentAllocationGrid
                  rows={allocationRows}
                  onAllocateChange={(id, val) =>
                    setAllocationRows((prev) =>
                      prev.map((r) => (r.invoiceId === id ? { ...r, allocate: val } : r)),
                    )
                  }
                  paymentTotal={paymentAmount}
                />
              </div>
            ) : loadingOpenInvoices ? (
              <p className="text-sm text-muted-foreground">Loading open invoices…</p>
            ) : resolvedAccountId == null ? (
              <p className="text-sm text-muted-foreground">
                No account ID provided. This payment will be recorded as an account credit.
              </p>
            ) : allocationRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No open invoices found for account #{resolvedAccountId}. The payment will land as an account credit.
              </p>
            ) : (
              <PaymentAllocationGrid
                rows={allocationRows}
                onAllocateChange={(id, val) =>
                  setAllocationRows((prev) =>
                    prev.map((r) => (r.invoiceId === id ? { ...r, allocate: val } : r)),
                  )
                }
                paymentTotal={paymentAmount}
              />
            )}

            </div>
            <div className="flex justify-between pt-4 shrink-0 mt-auto">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)}>Next</Button>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Step 3: Review                                                       */}
        {/* ------------------------------------------------------------------ */}
        {step === 3 && (
          <section aria-label="Step 3: Review" className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-1 pr-2 space-y-4">
              {/* Highlighted Amount */}
              <div className="flex flex-col items-center justify-center py-10 rounded-xl bg-gradient-to-b from-slate-50 to-white border shadow-sm">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Total Payment</span>
                <span className="text-5xl font-bold text-slate-900 tracking-tight">{fmt.format(paymentAmount)}</span>
              </div>

              {/* Core Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border p-4 bg-white shadow-sm flex flex-col justify-center">
                   <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Transaction Date</span>
                   <span className="text-sm font-medium">{formatDate(transactionDate)}</span>
                </div>
                <div className="rounded-xl border p-4 bg-white shadow-sm flex flex-col justify-center">
                   <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Payment Type</span>
                   <span className="text-sm font-medium">{paymentTypeCode}</span>
                </div>
              </div>

              {/* Additional Details */}
              <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
                <div className="bg-slate-50/50 border-b px-4 py-3 font-medium text-sm text-slate-700">
                  Allocation &amp; Reference
                </div>
                <div className="p-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                    <div className="flex flex-col gap-1">
                      <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Total Allocated</dt>
                      <dd className="font-medium text-slate-900">{fmt.format(totalAllocated)}</dd>
                    </div>

                    {paymentMethodCode && (
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Method</dt>
                        <dd className="font-medium text-slate-900">{paymentMethodCode}</dd>
                      </div>
                    )}

                    {paymentGatewayCode && (
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Gateway</dt>
                        <dd className="font-medium text-slate-900">{paymentGatewayCode}</dd>
                      </div>
                    )}

                    {pymtReferenceNo && (
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Reference</dt>
                        <dd className="font-medium text-slate-900">{pymtReferenceNo}</dd>
                      </div>
                    )}

                    {invoiceIdProp != null && (
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Invoice</dt>
                        <dd className="font-medium text-slate-900">#{invoiceIdProp}</dd>
                      </div>
                    )}

                    {resolvedAccountId != null && (
                      <div className="flex flex-col gap-1">
                        <dt className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Account</dt>
                        <dd className="font-medium text-slate-900">#{resolvedAccountId}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Overpayment warning */}
              {overPayment > 0 && (
                <div
                  role="alert"
                  className="rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
                >
                  OverPayment detected: {fmt.format(overPayment)} will land on account credit.
                </div>
              )}

              {/* Under-allocation notice */}
              {underAllocation > 0 && invoiceIdProp == null && (
                <div
                  role="note"
                  className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800"
                >
                  {fmt.format(underAllocation)} unallocated — will land as an account credit.
                </div>
              )}

              {notes && (
                <div className="rounded-xl border bg-slate-50/50 p-4 text-sm shadow-sm">
                  <span className="block text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Notes</span>
                  <span className="text-slate-700">{notes}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4 shrink-0 mt-auto">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={() => setStep(4)}>Confirm &amp; Submit</Button>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Step 4: Submit                                                       */}
        {/* ------------------------------------------------------------------ */}
        {step === 4 && (
          <section aria-label="Step 4: Submit" className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-1 pr-2 space-y-4">
              <div className="rounded-md border p-4 text-sm space-y-2">
              <p>
                Ready to post <strong>{fmt.format(paymentAmount)}</strong> on{" "}
                <strong>{formatDate(transactionDate)}</strong> via <strong>{paymentTypeCode}</strong>.
              </p>
              {overPayment > 0 && (
                <p className="text-yellow-700">
                  Note: {fmt.format(overPayment)} overpayment will be credited to the account.
                </p>
              )}
            </div>

            {createMut.isError && (
              <p className="text-sm text-destructive">
                Submission failed. Please try again.
              </p>
            )}
            </div>

            <div className="flex justify-between pt-4 shrink-0 mt-auto">
              <Button variant="outline" onClick={() => setStep(3)} disabled={createMut.isPending}>
                Back
              </Button>
              <Button
                onClick={() => createMut.mutate(buildPayload())}
                disabled={createMut.isPending}
              >
                {createMut.isPending ? "Submitting…" : "Submit Payment"}
              </Button>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
